import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import DateRangeCalendar from '../components/DateRangeCalendar';
import { ordersWord } from '../utils';

function CourierStatsTab({ showToast }) {
  const [periodType, setPeriodType] = useState('preset');
  const [periodDays, setPeriodDays] = useState(1);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCouriers, setExpandedCouriers] = useState(new Set());

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getApiParams = useCallback(() => {
    if (periodType === 'custom' && customFrom && customTo) {
      return { dateFrom: customFrom, dateTo: customTo };
    }
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - (periodDays - 1));
    return { dateFrom: formatDate(from), dateTo: formatDate(to) };
  }, [periodType, periodDays, customFrom, customTo]);

  const loadData = useCallback(async () => {
    if (periodType === 'custom' && (!customFrom || !customTo)) return;
    setIsLoading(true);
    try {
      const result = await api.getCourierStats(getApiParams());
      setData(result);
    } catch (error) {
      showToast('Ошибка загрузки данных курьеров', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [getApiParams, periodType, customFrom, customTo, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePreset = (days) => {
    setPeriodType('preset');
    setPeriodDays(days);
    setExpandedCouriers(new Set());
  };

  const handleCustomRange = (from, to) => {
    setCustomFrom(from);
    setCustomTo(to);
    setPeriodType('custom');
    setExpandedCouriers(new Set());
    setShowCalendar(false);
  };

  const toggleCourier = (courierId) => {
    setExpandedCouriers(prev => {
      const next = new Set(prev);
      if (next.has(courierId)) next.delete(courierId);
      else next.add(courierId);
      return next;
    });
  };

  const fmtDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}.${m}.${y}`;
  };

  const fmtMoney = (val) => {
    return Math.round(val).toLocaleString('ru-RU');
  };

  // Проверяем, доставлен ли заказ вовремя
  // delivery_time: "15:00-17:00", completed_at: ISO string
  // Возвращает: { time: "16:32", onTime: true/false } или null
  const getDeliveryStatus = (order) => {
    if (!order.completed_at) return null;
    const completed = new Date(order.completed_at);
    const hh = String(completed.getHours()).padStart(2, '0');
    const mm = String(completed.getMinutes()).padStart(2, '0');
    const time = `${hh}:${mm}`;

    if (!order.delivery_time) return { time, onTime: true };

    // Парсим конечное время диапазона "15:00-17:00" → 17:00
    const parts = order.delivery_time.split('-');
    const endTime = parts.length > 1 ? parts[1].trim() : parts[0].trim();
    const [endH, endM] = endTime.split(':').map(Number);
    const endMinutes = endH * 60 + endM;
    const completedMinutes = completed.getHours() * 60 + completed.getMinutes();

    return { time, onTime: completedMinutes <= endMinutes };
  };

  const couriers = data?.couriers || [];
  const totals = data?.totals || {};
  const commission = data?.commission_percent || 0;

  return (
    <div style={{ maxWidth: '900px' }}>
      {/* Фильтры периода */}
      <div style={{
        background: 'white', '--ch': '10px', padding: '12px 16px',
        marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {/* Период — общий переключатель .seg, как в «Статистике» (UX-аудит 25.10) */}
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="seg">
          {[
            { label: 'Сегодня', days: 1 },
            { label: '7 дней', days: 7 },
            { label: '14 дней', days: 14 },
            { label: '30 дней', days: 30 },
          ].map(p => (
            <button
              key={p.days}
              type="button"
              onClick={() => handlePreset(p.days)}
              className={`seg-btn${periodType === 'preset' && periodDays === p.days ? ' on' : ''}`}
            >
              {p.label}
            </button>
          ))}
          </div>

          <div style={{ position: 'relative' }}>
            <div className="seg">
            <button
              type="button"
              onClick={() => setShowCalendar(!showCalendar)}
              className={`seg-btn${periodType === 'custom' ? ' on' : ''}`}
            >
              {periodType === 'custom' && customFrom && customTo
                ? `${fmtDisplayDate(customFrom)} — ${fmtDisplayDate(customTo)}`
                : 'Выбрать период'}
            </button>
            </div>

            {showCalendar && (
              <DateRangeCalendar
                dateFrom={customFrom}
                dateTo={customTo}
                onSelect={handleCustomRange}
                onClose={() => setShowCalendar(false)}
              />
            )}
          </div>

          {data && (
            <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
              Комиссия компании: {commission}%
            </span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
          Загрузка...
        </div>
      ) : couriers.length === 0 ? (
        <div style={{
          background: 'white', '--ch': '10px', padding: '40px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center', color: '#999',
        }}>
          Нет данных за выбранный период
        </div>
      ) : (
        <>
          {/* Итоги */}
          <div style={{
            // итоги — фирменная плашка, без фиолетового градиента (UX-аудит 25.10)
            background: 'var(--primary-color)',
            '--ch': '10px', '--nfk': 1, padding: '16px 20px', marginBottom: '16px',
            color: 'white', display: 'flex', gap: '32px', flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>Заказов</div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>{totals.total_orders}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>Выручка доставки</div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>{fmtMoney(totals.total_delivery_cost)} &#8381;</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>Выплата курьерам</div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>{fmtMoney(totals.total_courier_cost)} &#8381;</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', opacity: 0.8 }}>Прибыль компании</div>
              <div style={{ fontSize: '22px', fontWeight: 700 }}>
                {fmtMoney(totals.total_delivery_cost - totals.total_courier_cost)} &#8381;
              </div>
            </div>
          </div>

          {/* Список курьеров */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {couriers.map(courier => {
              const isExpanded = expandedCouriers.has(courier.courier_id);
              return (
                <div key={courier.courier_id} style={{
                  background: 'white', '--ch': '10px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden',
                }}>
                  {/* Заголовок курьера */}
                  <div
                    onClick={() => toggleCourier(courier.courier_id)}
                    style={{
                      padding: '12px 16px', cursor: 'pointer',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      borderBottom: isExpanded ? '1px solid #EBEDEC' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 600 }}>{courier.courier_name}</span>
                      <span style={{
                        fontSize: '12px', color: '#999', background: '#F4F4EF',
                        padding: '2px 8px', '--ch': '8px',
                      }}>
                        {courier.orders_count} {ordersWord(courier.orders_count)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#999' }}>Клиент</div>
                        <div style={{ fontSize: '14px', fontWeight: 600 }}>{fmtMoney(courier.total_delivery_cost)} &#8381;</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: '#999' }}>Курьер</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#359250' }}>{fmtMoney(courier.total_courier_cost)} &#8381;</div>
                      </div>
                      <span style={{ fontSize: '14px', color: '#999', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
                        ▼
                      </span>
                    </div>
                  </div>

                  {/* Список заказов */}
                  {isExpanded && (
                    <div>
                      <div style={{
                        display: 'grid', gridTemplateColumns: '50px 80px 90px 70px 1fr 80px 80px',
                        padding: '6px 16px', fontSize: '11px', color: '#999', fontWeight: 600,
                        borderBottom: '1px solid #F4F4EF',
                      }}>
                        <div>#</div>
                        <div>Дата</div>
                        <div>Время</div>
                        <div>Доставлен</div>
                        <div>Адрес</div>
                        <div style={{ textAlign: 'right' }}>Клиент</div>
                        <div style={{ textAlign: 'right' }}>Курьер</div>
                      </div>
                      {courier.orders.map(order => {
                        const ds = getDeliveryStatus(order);
                        return (
                        <div key={order.order_id} style={{
                          display: 'grid', gridTemplateColumns: '50px 80px 90px 70px 1fr 80px 80px',
                          padding: '6px 16px', fontSize: '13px',
                          borderBottom: '1px solid #f8f8f8',
                        }}>
                          <div style={{ color: '#999' }}>{order.order_id}</div>
                          <div>{fmtDisplayDate(order.delivery_date)}</div>
                          <div style={{ color: '#666' }}>{order.delivery_time || '—'}</div>
                          <div>
                            {ds ? (
                              <span style={{
                                color: ds.onTime ? '#359250' : '#CB4D45',
                                fontWeight: 500,
                              }}>
                                {ds.time}
                              </span>
                            ) : (
                              <span style={{ color: '#ccc' }}>—</span>
                            )}
                          </div>
                          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#666' }}>
                            {order.delivery_address || '—'}
                          </div>
                          <div style={{ textAlign: 'right' }}>{fmtMoney(order.delivery_cost)} &#8381;</div>
                          <div style={{ textAlign: 'right', color: '#359250', fontWeight: 500 }}>{fmtMoney(order.courier_delivery_cost)} &#8381;</div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default CourierStatsTab;
